/** Sites, protocol compatibility, means and running sums for skinfold entry. */

export const skinfoldEditMeasurementCacheKey = (patientId, measurementId) =>
  `lorie:anthropometry:edit-measurement:${patientId}:${measurementId}`;

export const UNUSUAL_SKINFOLD_MM = 45;

export const SKINFOLD_SITES = [
  { key: "triceps", label: "Tricipital", shortLabel: "DCT", apiField: "triceps_skinfold" },
  { key: "biceps", label: "Bicipital", shortLabel: "DCB", apiField: "biceps_skinfold" },
  { key: "subscapular", label: "Subescapular", shortLabel: "DCSE", apiField: "subscapular_skinfold" },
  { key: "suprailiac", label: "Supra-ilíaca", shortLabel: "DCSI", apiField: "suprailiac_skinfold" },
  { key: "abdominal", label: "Abdominal", shortLabel: "DCA", apiField: "abdominal_skinfold" },
  { key: "chest", label: "Peitoral", shortLabel: "DCP", apiField: "chest_skinfold" },
  { key: "midaxillary", label: "Axilar média", shortLabel: "DCAM", apiField: "midaxillary_skinfold" },
  { key: "thigh", label: "Coxa", shortLabel: "DCC", apiField: "thigh_skinfold" },
  { key: "medial_calf", label: "Panturrilha", shortLabel: "DCPM", apiField: "medial_calf_skinfold" },
];

export const SKINFOLD_PROTOCOLS = [
  {
    id: "JACKSON_POLLOCK_3",
    label: "Jackson Pollock 3",
    sexSpecific: true,
    sitesMale: ["chest", "abdominal", "thigh"],
    sitesFemale: ["triceps", "suprailiac", "thigh"],
  },
  {
    id: "JACKSON_POLLOCK_6",
    label: "Jackson Pollock 6",
    sites: ["chest", "midaxillary", "triceps", "subscapular", "abdominal", "thigh"],
  },
  {
    id: "JACKSON_POLLOCK_7",
    label: "Jackson Pollock 7",
    sites: ["chest", "midaxillary", "triceps", "subscapular", "abdominal", "suprailiac", "thigh"],
  },
  {
    id: "PROTOCOL_6_SKINFOLDS",
    label: "Protocolo de 6 dobras",
    sites: ["triceps", "subscapular", "suprailiac", "abdominal", "thigh", "medial_calf"],
  },
  {
    id: "DURNIN_WOMERSLEY",
    label: "Durnin & Womersley",
    sites: ["biceps", "triceps", "subscapular", "suprailiac"],
  },
  {
    id: "FAULKNER",
    label: "Faulkner",
    sites: ["triceps", "subscapular", "suprailiac", "abdominal"],
  },
  {
    id: "PETROSKI",
    label: "Petroski",
    sites: ["triceps", "subscapular", "suprailiac", "midaxillary"],
  },
  {
    id: "GUEDES",
    label: "Guedes",
    sites: ["triceps", "subscapular", "suprailiac"],
  },
  {
    id: "SLAUGHTER",
    label: "Slaughter",
    anyOfSiteSets: [
      ["triceps", "subscapular"],
      ["triceps", "medial_calf"],
    ],
  },
];

const SITE_BY_KEY = SKINFOLD_SITES.reduce((acc, site) => {
  acc[site.key] = site;
  return acc;
}, {});

export const emptySiteState = (key) => ({
  key,
  measures: ["", "", ""],
  mean: null,
});

export const createEmptySkinfoldState = () =>
  SKINFOLD_SITES.map((site) => emptySiteState(site.key));

export const parseMeasure = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) ? number : null;
};

/** Arithmetic mean of filled measures (1–3). Returns null if none filled. */
export const computeSiteMean = (measures = []) => {
  const values = measures.map(parseMeasure).filter((value) => value != null);
  if (values.length === 0) {
    return null;
  }
  const sum = values.reduce((acc, value) => acc + value, 0);
  return Math.round((sum / values.length) * 10) / 10;
};

export const isSiteFilled = (siteState) => siteState?.mean != null;

export const getFilledSiteKeys = (sitesState = []) =>
  sitesState.filter(isSiteFilled).map((site) => site.key);

export const getSiteMeanMap = (sitesState = []) => {
  const map = {};
  sitesState.forEach((site) => {
    if (site.mean != null) {
      map[site.key] = site.mean;
    }
  });
  return map;
};

export const isMalePatient = (patient) => {
  const gender = patient?.gender || patient?.account?.gender;
  return gender === "MALE" || gender === "Masculino";
};

export const getProtocolRequiredSites = (protocol, patient) => {
  if (!protocol) {
    return [];
  }
  if (protocol.anyOfSiteSets) {
    return [];
  }
  if (protocol.sexSpecific) {
    return isMalePatient(patient) ? protocol.sitesMale : protocol.sitesFemale;
  }
  return protocol.sites || [];
};

export const getMissingSitesForProtocol = (protocol, filledKeys, patient) => {
  const filled = new Set(filledKeys);

  if (protocol.anyOfSiteSets) {
    const anyComplete = protocol.anyOfSiteSets.some((set) =>
      set.every((key) => filled.has(key))
    );
    if (anyComplete) {
      return [];
    }
    const preferred = protocol.anyOfSiteSets[0];
    return preferred
      .filter((key) => !filled.has(key))
      .map((key) => SITE_BY_KEY[key]?.label || key);
  }

  const required = getProtocolRequiredSites(protocol, patient);
  return required
    .filter((key) => !filled.has(key))
    .map((key) => SITE_BY_KEY[key]?.label || key);
};

export const isProtocolCompatible = (protocol, filledKeys, patient) =>
  getMissingSitesForProtocol(protocol, filledKeys, patient).length === 0;

export const sumSiteMeans = (meanMap, siteKeys) => {
  if (!siteKeys?.length) {
    return null;
  }
  if (!siteKeys.every((key) => meanMap[key] != null)) {
    return null;
  }
  const sum = siteKeys.reduce((acc, key) => acc + meanMap[key], 0);
  return Math.round(sum * 10) / 10;
};

export const hasAnyMeasure = (sitesState = []) =>
  sitesState.some((site) => site.measures.some((measure) => parseMeasure(measure) != null));

export const hasUnusualMeasure = (measures = []) =>
  measures.some((measure) => {
    const value = parseMeasure(measure);
    return value != null && value > UNUSUAL_SKINFOLD_MM;
  });

export const formatMm = (value) => {
  if (value == null) {
    return "—";
  }
  return `${String(value).replace(".", ",")} mm`;
};

const normalizeReadingSite = (site) => {
  if (site == null || site === "") {
    return null;
  }
  if (typeof site === "string") {
    return site.toLowerCase();
  }
  if (typeof site === "object" && site.value) {
    return String(site.value).toLowerCase();
  }
  return String(site).toLowerCase();
};

const readingMeasureValue = (reading, index) => {
  const snakeKey = `measure_${index}`;
  const camelKey = `measure${index}`;
  const value = reading?.[snakeKey] ?? reading?.[camelKey];
  return value != null ? String(value) : "";
};

const anthropometrySkinfoldValue = (anthropometry, apiField) => {
  if (!anthropometry || !apiField) {
    return null;
  }
  if (anthropometry[apiField] != null && anthropometry[apiField] !== "") {
    return anthropometry[apiField];
  }
  const camelField = apiField.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  const camelValue = anthropometry[camelField];
  return camelValue != null && camelValue !== "" ? camelValue : null;
};

export const sitesStateFromMeasurement = (measurement) => {
  const base = createEmptySkinfoldState();
  const readings = measurement?.skinfold_readings || measurement?.skinfoldReadings || [];
  const anthropometry = measurement?.anthropometry || {};

  const bySite = readings.reduce((acc, reading) => {
    const siteKey = normalizeReadingSite(reading?.site);
    if (siteKey) {
      acc[siteKey] = reading;
    }
    return acc;
  }, {});

  return base.map((site) => {
    const reading = bySite[site.key];
    if (reading) {
      let measures = [
        readingMeasureValue(reading, 1),
        readingMeasureValue(reading, 2),
        readingMeasureValue(reading, 3),
      ];
      const meanValue = reading.mean_mm ?? reading.meanMm;
      const mean =
        meanValue != null
          ? Number(meanValue)
          : computeSiteMean(measures);

      if (mean != null && !measures.some((measure) => measure !== "")) {
        measures = [String(mean).replace(".", ","), "", ""];
      }

      return { ...site, measures, mean };
    }

    const catalog = SITE_BY_KEY[site.key];
    const value = catalog ? anthropometrySkinfoldValue(anthropometry, catalog.apiField) : null;
    if (value == null || value === "") {
      return site;
    }
    const measures = [String(value).replace(".", ","), "", ""];
    return {
      ...site,
      measures,
      mean: computeSiteMean(measures),
    };
  });
};

export const protocolsFromMeasurement = (measurement) => {
  const protocols =
    measurement?.selected_skinfold_protocols ||
    measurement?.selectedSkinfoldProtocols ||
    [];
  const knownIds = new Set(SKINFOLD_PROTOCOLS.map((protocol) => protocol.id));

  return protocols
    .map((protocol) => {
      if (typeof protocol === "string") {
        return protocol;
      }
      return protocol?.id || protocol?.name || protocol?.value || null;
    })
    .filter((id) => id && knownIds.has(id));
};

export const measurementHasSkinfolds = (measurement) => {
  if (!measurement) {
    return false;
  }
  const readings = measurement.skinfold_readings || measurement.skinfoldReadings || [];
  if (readings.length > 0) {
    return true;
  }
  return getFilledSiteKeys(sitesStateFromMeasurement(measurement)).length > 0;
};

export const buildAnthropometryFromSites = (sitesState = []) => {
  const anthropometry = {};
  sitesState.forEach((site) => {
    if (site.mean == null) {
      return;
    }
    const catalog = SITE_BY_KEY[site.key];
    if (catalog?.apiField) {
      anthropometry[catalog.apiField] = site.mean;
    }
  });
  return anthropometry;
};

export const buildSkinfoldReadingsPayload = (sitesState = []) =>
  sitesState
    .filter((site) => site.mean != null)
    .map((site) => ({
      site: site.key,
      measure_1: parseMeasure(site.measures[0]),
      measure_2: parseMeasure(site.measures[1]),
      measure_3: parseMeasure(site.measures[2]),
      mean_mm: site.mean,
    }));

export const buildSkinfoldSavePayloadV2 = ({
  nutritionistId,
  patientId,
  visitDate,
  sitesState,
  selectedProtocols = [],
  patient,
  previousMeasurement,
  measurementId,
}) => {
  const anthropometry = buildAnthropometryFromSites(sitesState);
  const skinfoldReadings = buildSkinfoldReadingsPayload(sitesState);
  const knownProtocolIds = new Set(SKINFOLD_PROTOCOLS.map((protocol) => protocol.id));
  const validProtocols = selectedProtocols.filter((id) => knownProtocolIds.has(id));

  const payload = {
    nutritionist_id: nutritionistId,
    patient_id: Number(patientId),
    visit_date: visitDate,
    ...(Object.keys(anthropometry).length > 0 ? { anthropometry } : {}),
    ...(skinfoldReadings.length > 0 ? { skinfold_readings: skinfoldReadings } : {}),
    ...(validProtocols.length > 0
      ? { selected_skinfold_protocols: validProtocols }
      : {}),
  };

  const weight = patient?.body_measurement?.weight;
  const height = patient?.body_measurement?.height;
  const previousMetabolic = previousMeasurement?.metabolic_data;
  const resolvedWeightKg = previousMetabolic?.weight_kg ?? weight ?? null;
  const resolvedHeightCm = previousMetabolic?.height_cm ?? height ?? null;
  if (resolvedWeightKg != null && resolvedHeightCm != null) {
    payload.metabolic_data = {
      weight_kg: resolvedWeightKg,
      height_cm: resolvedHeightCm,
    };
  }

  const resolvedMeasurementId = measurementId;

  const previousCircumference = previousMeasurement?.circumference;
  if (!resolvedMeasurementId && previousCircumference) {
    const circumference = {};
    [
      "biceps",
      "triceps",
      "hips",
      "calf",
      "neck",
      "waist",
      "thigh",
      "forearm",
      "wrist",
      "chest",
      "shoulder",
    ].forEach((field) => {
      if (previousCircumference[field] != null) {
        circumference[field] = previousCircumference[field];
      }
    });
    if (Object.keys(circumference).length > 0) {
      payload.circumference = circumference;
    }
  }

  const previousCustoms = (previousMeasurement?.custom_circumferences || []).filter(
    (item) => item?.label
  );
  if (!resolvedMeasurementId && previousCustoms.length > 0) {
    payload.custom_circumferences = previousCustoms.map((item) => ({
      label: item.label,
      value_cm: item.value_cm,
    }));
  }

  if (resolvedMeasurementId != null && resolvedMeasurementId !== "") {
    payload.id = Number(resolvedMeasurementId);
  }

  return payload;
};

export const buildSkinfoldDiscardPayload = ({
  nutritionistId,
  patientId,
  visitDate,
  sitesState,
  selectedProtocols = [],
  measurementId,
}) => {
  const knownProtocolIds = new Set(SKINFOLD_PROTOCOLS.map((protocol) => protocol.id));
  const validProtocols = selectedProtocols.filter((id) => knownProtocolIds.has(id));
  const resolvedId = Number(measurementId);

  const payload = {
    nutritionist_id: nutritionistId,
    patient_id: Number(patientId),
    visit_date: visitDate,
    anthropometry: buildAnthropometryFromSites(sitesState),
    skinfold_readings: buildSkinfoldReadingsPayload(sitesState),
    selected_skinfold_protocols: validProtocols,
    replace_skinfold_data: true,
  };

  if (Number.isFinite(resolvedId) && resolvedId > 0) {
    payload.id = resolvedId;
  }

  return payload;
};
